import styled from "styled-components/native";
import OptionHeader from "../OptionHeader";
import { Spacer } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { getProductColor } from "@scenes/pdp/utils/utils";
import { getProductLevelColor } from "@components/molecules/MatchStoneGalleryModel/Utils";
import { useTheme } from "styled-components/native";
import { RectangularTextButtonWithPriceProps } from "./types";
import RectangularTextViewWithDetail from "../RectangularTextViewWithDetail";
import { memo, useEffect, useRef, useState } from "react";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { isKeyPresent } from "@scenes/configurator/JewelryConfigurator/utils";
import JewelryBuilderErrorMessage from "../JewelryBuilderErrorMessage";
import ResetButtonComponent from "../ResetButton";
import { FlatList } from "react-native";
import { screenWidth, SCROLL_STEP } from "@utils/constants";
import React from "react";
import { DefaultTheme } from "styled-components/native";

const RectangularTextButtonWithPrice = ({
  productType,
  configObject,
  showCarousel,
  fromReviewPage,
  isOpen,
  onToggle,
  fromOrderCart,
  fromRequest,
}: RectangularTextButtonWithPriceProps) => {
  const theme = useTheme();
  const productColor = getProductLevelColor(productType);
  const borderColor = getProductColor(productType);
  const {
    updateJewelryConfigInfo,
    jcSelectedOption,
    validateRequiredOptions,
    fetchValidationMessage,
    addToCartPressed,
  } = useJewelryConfiguratorState();

  useEffect(() => {}, [addToCartPressed]);

  const savedOptionValue =
    jcSelectedOption.current.option_value[configObject.option_type];
  const [selectedItem, setSelectedItem] = useState(
    savedOptionValue != null && savedOptionValue !== ""
      ? savedOptionValue
      : configObject?.selected_item
  );

  useEffect(() => {
    const liveValue =
      jcSelectedOption.current.option_value[configObject.option_type];
    setSelectedItem(
      liveValue != null && liveValue !== ""
        ? liveValue
        : configObject?.selected_item
    );
  }, [
    configObject?.selected_item,
    jcSelectedOption.current.option_value[configObject.option_type],
  ]);

  const flatListRef = useRef(null);
  const [scrollData, setScrollData] = useState({
    contentOffset: 0,
    contentWidth: 0,
    layoutWidth: 0,
    canScrollNext: true,
    canScrollPrev: false,
  });

  const calculateItemWidth = (item: any) => {
    const baseWidth = 100;
    const labelLength = item.label.length;
    const padding = 32; // Left and right padding (16 * 2)
    const iconWidth = 40; // Space for icon

    // Calculate width based on label length with min/max bounds
    // Using approximate 8px per character for font size 16
    const textWidth = labelLength * 8;
    const calculatedWidth = Math.max(
      baseWidth,
      Math.min(screenWidth * 0.9, textWidth + padding + iconWidth)
    );

    return calculatedWidth;
  };

  const handleScroll = (event: any) => {
    const {
      contentOffset: { x },
      contentSize: { width: contentWidth },
      layoutMeasurement: { width: layoutWidth },
    } = event.nativeEvent;

    // Calculate if we can scroll in either direction based on content
    const canScrollPrev = x > 5; // Small tolerance for precision
    const canScrollNext = x + layoutWidth < contentWidth - 5; // Small tolerance

    setScrollData({
      contentOffset: x,
      contentWidth,
      layoutWidth,
      canScrollNext,
      canScrollPrev,
    });
  };

  const scrollList = (direction: string) => {
    let newOffset =
      direction === "left"
        ? Math.max(0, scrollData.contentOffset - SCROLL_STEP)
        : Math.min(
            scrollData.contentWidth === 0
              ? SCROLL_STEP
              : scrollData.contentWidth,
            scrollData.contentOffset + SCROLL_STEP
          );
    (flatListRef.current as any)?.scrollToOffset({
      offset: newOffset,
      animated: true,
    });
  };

  const RenderedItem = memo(({ item }: any) => {
    return (
      <RectangularTextViewWithDetail
        backgroundColor={
          selectedItem === item?.value
            ? theme[productColor]
            : theme["option-bg-static"]
        }
        borderColor={
          selectedItem === item?.value
            ? theme[borderColor as keyof DefaultTheme]
            : theme["option-bg-static"]
        }
        isSelected={selectedItem === item?.value}
        item={item}
        numberOfLines={1}
        onPress={(selected) => {
          setSelectedItem(selected.value);
          updateJewelryConfigInfo(configObject?.option_type, [
            configObject,
            selected.slug || selected.value,
          ]);
          if (
            isKeyPresent(
              configObject.option_type,
              jcSelectedOption.current.option_error
            )
          ) {
            validateRequiredOptions();
          }
        }}
      />
    );
  });

  const getItemLayout = (data: any, index: number) => {
    let offset = 20;
    for (let i = 0; i < index; i++) {
      offset += calculateItemWidth(data[i]) + 15;
    }

    return {
      length: calculateItemWidth(data[index]),
      offset,
      index,
    };
  };

  return (
    <ContainerView>
      <OptionHeader
        title={configObject.option_name}
        value={selectedItem}
        productType={productType}
        showCarousel={showCarousel}
        helperText={configObject.option_helper_text}
        leftSliderDisabled={!scrollData.canScrollPrev}
        rightSliderDisabled={!scrollData.canScrollNext}
        onLeftSliderPress={() => {
          scrollData.canScrollPrev && scrollList("left");
        }}
        onRightSliderPress={() => {
          scrollData.canScrollNext && scrollList("right");
        }}
        fromReviewPage={fromReviewPage}
        isOpen={isOpen}
        onToggle={onToggle}
        fromOrderCart={fromOrderCart}
        fromRequest={fromRequest}
        disableToggleButton={configObject?.disable_modify_variation ?? false}
      />
      {(isOpen || !fromReviewPage) && (
        <>
          <Spacer size={SpaceType.Extra} />
          {showCarousel ? (
            <FlatList
              ref={flatListRef}
              data={configObject.option_value}
              contentContainerStyle={{ gap: SpaceType.Double }}
              horizontal={showCarousel}
              onScroll={handleScroll}
              getItemLayout={getItemLayout}
              showsHorizontalScrollIndicator={false}
              scrollEventThrottle={16}
              snapToAlignment="start"
              decelerationRate="fast"
              renderItem={({ item }) => <RenderedItem item={item} />}
            />
          ) : (
            <Container>
              {configObject.option_value?.map((item) => (
                <RenderedItem item={item} key={item.value} />
              ))}
            </Container>
          )}
          <BottomContainer
            showError={isKeyPresent(
              configObject.option_type,
              jcSelectedOption.current.option_error
            )}
          >
            {isKeyPresent(
              configObject.option_type,
              jcSelectedOption.current.option_error
            ) && (
              <JewelryBuilderErrorMessage
                message={fetchValidationMessage(
                  configObject.option_name,
                  false
                )}
              />
            )}
            {configObject?.reset_button_visible && (
              <ResetButtonComponent productType={productType} />
            )}
          </BottomContainer>
        </>
      )}
    </ContainerView>
  );
};

const ContainerView = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: 8px;
  padding: 12px 16px;
`;

const Container = styled.View<{ justifyContent?: string }>`
  gap: ${SpaceType.Basic}px;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: ${({ justifyContent }: { justifyContent: string }) =>
    justifyContent ?? "flex-start"};
`;

const BottomContainer = styled.View<{
  showError: boolean;
}>`
  flex-direction: row;
  justify-content: ${({ showError }: { showError: boolean }) =>
    showError ? "space-between" : "flex-end"};
  margin-top: 8px;
`;

export default RectangularTextButtonWithPrice;
